import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button,
} from "@mui/material";
import {
  AutoAwesome,
  CheckCircle,
  SmartToy,
  OpenInNew,
  ChatBubbleOutline,
  ExpandMore,
  Add,
  Wifi,
  WifiOff,
} from "@mui/icons-material";
import {
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useGetTeacherSubjectsQuery, useGetClassroomSubjectsQuery } from "../../../../../../app/questionApiSlice";
import {
  useGetTopicsBySubjectAndLevelQuery,
} from "../../../../../../app/quizApiSlice";
import { useGetClassesPaginatedQuery } from "../../../../classroom/classApiSlice";
import { selectCurrentUser } from "../../../../auth/authSlice";
import { FilledButton } from "../../../../../../customComponents/styled/styledButtons";
import { getApiBaseUrl } from "../../../../../../helpers/apiUrl";
import { localToUTC } from "../../../../../../helpers/dateUtils";

const QUIZ_TYPES = ["test", "exam", "assignment", "quiz"];

const AIQuiz = () => {
  const navigate = useNavigate();
  const authData = useSelector((state) => state.auth);
  const currentUser = useSelector(selectCurrentUser);
  const accessToken = authData?.access_token;
  const baseUrl = getApiBaseUrl();

  const userRoles = currentUser?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isTeacher = userRoles.includes("teacher");
  const isExaminer = userRoles.includes("examiner");
  // Examiner who is not also a teacher → use all-classes mode
  const useAllClasses = isExaminer && !isTeacher;

  const { data: teacherSubjects = [], isLoading: isLoadingSubjects } =
    useGetTeacherSubjectsQuery(undefined, { skip: useAllClasses });

  const { data: allClassroomsData } = useGetClassesPaginatedQuery(
    { page: 1, per_page: 200 },
    { skip: !useAllClasses }
  );

  // Form state
  const [isOffline, setIsOffline] = useState(false);
  const [classroomId, setClassroomId] = useState("");
  const [subject, setSubject] = useState("");
  const [type, setType] = useState("test");
  const [dueAt, setDueAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [duration, setDuration] = useState(30);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customTopicInput, setCustomTopicInput] = useState("");
  const [instruction, setInstruction] = useState("");
  const [extraContext, setExtraContext] = useState("");

  // Fetch classroom subjects for examiner
  const { data: classroomSubjectsData = [] } = useGetClassroomSubjectsQuery(
    { classroom_id: classroomId },
    { skip: !useAllClasses || !classroomId }
  );

  // Derived classrooms
  const classrooms = useMemo(() => {
    if (useAllClasses) {
      return allClassroomsData?.classrooms || [];
    }
    const classMap = new Map();
    teacherSubjects.forEach((s) => {
      if (!classMap.has(s.classroom_id)) {
        classMap.set(s.classroom_id, {
          id: s.classroom_id,
          name: s.classroom,
          level: s.level,
        });
      }
    });
    return Array.from(classMap.values());
  }, [useAllClasses, teacherSubjects, allClassroomsData]);

  // AI streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [executingTool, setExecutingTool] = useState(null);
  const [aiOutput, setAiOutput] = useState("");
  const [createdQuiz, setCreatedQuiz] = useState(null);
  const [error, setError] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const [sessionToolsUsed, setSessionToolsUsed] = useState(new Set());
  const [hasApprovedTopics, setHasApprovedTopics] = useState(false);

  const selectedClassroom = useMemo(
    () => classrooms.find((c) => c.id === parseInt(classroomId)),
    [classrooms, classroomId]
  );

  const subjects = useMemo(() => {
    if (useAllClasses) {
      return classroomSubjectsData.map((s) => ({ id: s.id, name: s.name, classroom_id: parseInt(classroomId) }));
    }
    if (!classroomId) return [];
    return teacherSubjects.filter(
      (s) => s.classroom_id === parseInt(classroomId)
    );
  }, [useAllClasses, classroomSubjectsData, teacherSubjects, classroomId]);

  const { data: topicsData = {}, isFetching: isFetchingTopics } =
    useGetTopicsBySubjectAndLevelQuery(
      { subject, level: selectedClassroom?.level },
      { skip: !subject || !selectedClassroom?.level }
    );
  const availableTopics = topicsData.topics || [];

  const handleClassroomChange = (e) => {
    setClassroomId(e.target.value);
    setSubject("");
    setSelectedTopics([]);
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
    setSelectedTopics([]);
  };

  const handleTopicToggle = (topicName) => {
    setSelectedTopics((prev) =>
      prev.includes(topicName)
        ? prev.filter((t) => t !== topicName)
        : [...prev, topicName]
    );
  };

  const handleAddCustomTopic = () => {
    const trimmed = customTopicInput.trim();
    if (trimmed && !selectedTopics.includes(trimmed)) {
      setSelectedTopics((prev) => [...prev, trimmed]);
    }
    setCustomTopicInput("");
  };

  const buildMessage = useCallback(() => {
    const lines = [
      `Create a ${type} quiz with the following details:`,
      `- Mode: ${isOffline ? "offline (for printing/downloading only, no students will be assigned)" : "online"}`,
      `- Class: ${selectedClassroom?.name} (${selectedClassroom?.level})`,
      `- Subject: ${subject}`,
    ];
    if (selectedTopics.length > 0) {
      lines.push(`- Topics: ${selectedTopics.join(", ")}`);
    }
    lines.push(`- Duration: ${duration} minutes`);
    if (!isOffline) {
      if (dueAt) lines.push(`- Available from: ${localToUTC(dueAt)}`);
      if (expiresAt) lines.push(`- Expires: ${localToUTC(expiresAt)}`);
      lines.push(`- Participants: whole class`);
    }
    if (instruction) lines.push(`- Instructions: ${instruction}`);
    if (extraContext) lines.push(``, `Additional context: ${extraContext}`);
    return lines.join("\n");
  }, [type, isOffline, selectedClassroom, subject, selectedTopics, duration, dueAt, expiresAt, instruction, extraContext]);

  const handleGenerate = useCallback(async () => {
    if (!classroomId || !subject || !type || !duration) {
      setError(
        "Please fill in all required fields (class, subject, type, duration)."
      );
      return;
    }
    setError("");
    setAiOutput("");
    setCreatedQuiz(null);
    setExecutingTool(null);
    setIsStreaming(true);
    setPendingMessage("");
    setSessionToolsUsed(new Set());
    setHasApprovedTopics(false);

    const teacher_context = teacherSubjects.map((s) => ({
      name: s.name,
      level: s.level,
      classroom: s.classroom,
      classroom_id: s.classroom_id,
    }));

    const message = buildMessage();
    setPendingMessage(message);

    try {
      const response = await fetch(`${baseUrl}/quiz/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ message, history: [], teacher_context, is_offline: isOffline }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.message || `Request failed (${response.status})`
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let lineBuffer = "";
      let aiContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines[lines.length - 1];

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line.startsWith("data: ")) continue;
          const dataStr = line.substring(6).trim();
          let parsed;
          try {
            parsed = JSON.parse(dataStr);
          } catch {
            continue;
          }

          if (parsed.type === "text") {
            aiContent += parsed.content;
            setAiOutput(aiContent);
          } else if (parsed.type === "tool_call") {
            setExecutingTool(parsed.name);
            setSessionToolsUsed((prev) => new Set([...prev, parsed.name]));
          } else if (parsed.type === "available_topics") {
            if (parsed.has_topics) setHasApprovedTopics(true);
          } else if (parsed.type === "quiz_created") {
            setCreatedQuiz(parsed.quiz);
            setExecutingTool(null);
          } else if (parsed.type === "error") {
            setError(parsed.content || "An error occurred");
          } else if (parsed.type === "done") {
            setExecutingTool(null);
          }
        }
      }
    } catch (err) {
      setError(err.message || "Failed to generate quiz");
    } finally {
      setIsStreaming(false);
      setExecutingTool(null);
    }
  }, [classroomId, subject, type, dueAt, expiresAt, duration, teacherSubjects, baseUrl, accessToken, buildMessage]);

  const handleConfirm = useCallback(async () => {
    setError("");
    setExecutingTool(null);
    setIsStreaming(true);

    const teacher_context = teacherSubjects.map((s) => ({
      name: s.name,
      level: s.level,
      classroom: s.classroom,
      classroom_id: s.classroom_id,
    }));

    const history = [
      { role: "user", content: pendingMessage },
      { role: "assistant", content: aiOutput },
    ];

    try {
      const response = await fetch(`${baseUrl}/quiz/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          message: "Yes, everything looks good. Please create the quiz now.",
          history,
          teacher_context,
          is_offline: isOffline,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let lineBuffer = "";
      let aiContent = aiOutput;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines[lines.length - 1];

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line.startsWith("data: ")) continue;
          const dataStr = line.substring(6).trim();
          let parsed;
          try {
            parsed = JSON.parse(dataStr);
          } catch {
            continue;
          }

          if (parsed.type === "text") {
            aiContent += parsed.content;
            setAiOutput(aiContent);
          } else if (parsed.type === "tool_call") {
            setExecutingTool(parsed.name);
          } else if (parsed.type === "quiz_created") {
            setCreatedQuiz(parsed.quiz);
            setExecutingTool(null);
          } else if (parsed.type === "error") {
            setError(parsed.content || "An error occurred");
          } else if (parsed.type === "done") {
            setExecutingTool(null);
          }
        }
      }
    } catch (err) {
      setError(err.message || "Failed to create quiz");
    } finally {
      setIsStreaming(false);
      setExecutingTool(null);
    }
  }, [pendingMessage, aiOutput, teacherSubjects, baseUrl, accessToken]);

  const toolCallLabel = (name) => {
    const labels = {
      get_available_topics: "Looking up available topics...",
      get_classroom_students: "Fetching student list...",
      create_quiz: "Creating quiz...",
    };
    return labels[name] || "Processing...";
  };

  const canSubmit =
    !!classroomId && !!subject && !!type && !!duration &&
    (isOffline || (!!dueAt && !!expiresAt));

  if (isLoadingSubjects) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600, mb: 3 }}
      >
        Create Quiz with AI
      </Typography>

      <Grid container spacing={3}>
        {/* ── Main Form ── */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
              borderRadius: "12px",
              border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* Quiz Mode Toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                Quiz Mode:
              </Typography>
              <ToggleButtonGroup
                value={isOffline ? "offline" : "online"}
                exclusive
                onChange={(_, val) => {
                  if (val !== null) setIsOffline(val === "offline");
                }}
                size="small"
              >
                <ToggleButton value="online" sx={{ gap: 0.75, textTransform: "none", px: 2 }}>
                  <Wifi fontSize="small" />
                  Online
                </ToggleButton>
                <ToggleButton value="offline" sx={{ gap: 0.75, textTransform: "none", px: 2 }}>
                  <WifiOff fontSize="small" />
                  Offline
                </ToggleButton>
              </ToggleButtonGroup>
              {isOffline && (
                <Typography variant="caption" color="text.secondary">
                  No students will be assigned.
                </Typography>
              )}
            </Box>

            {/* Row 1: Class + Subject */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Class *</InputLabel>
                  <Select
                    value={classroomId}
                    onChange={handleClassroomChange}
                    label="Class *"
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">
                      <em>Select class</em>
                    </MenuItem>
                    {classrooms.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                        {c.level && (
                          <Chip
                            label={c.level}
                            size="small"
                            sx={{ ml: 1 }}
                            variant="outlined"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!classroomId}>
                  <InputLabel>Subject *</InputLabel>
                  <Select
                    value={subject}
                    onChange={handleSubjectChange}
                    label="Subject *"
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">
                      <em>Select subject</em>
                    </MenuItem>
                    {subjects.map((s) => (
                      <MenuItem key={s.id} value={s.name}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Row 2: Quiz Type + Duration */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Quiz Type *</InputLabel>
                  <Select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    label="Quiz Type *"
                    sx={{ borderRadius: "10px" }}
                  >
                    {QUIZ_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes) *"
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  inputProps={{ min: 5, max: 240 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
            </Grid>

            {/* Row 3: Available from + Expires at (online only) */}
            {!isOffline && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Available from *"
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  placeholder=""
                  error={!dueAt}
                  helperText={!dueAt ? "Required — when should the quiz become accessible?" : ""}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expires at *"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  placeholder=""
                  error={!expiresAt}
                  helperText={!expiresAt ? "Required — when should the quiz stop accepting submissions?" : ""}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
            </Grid>
            )}

            {/* Topics */}
            {subject && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                >
                  Topics{" "}
                  {isFetchingTopics && (
                    <CircularProgress size={12} sx={{ ml: 0.5 }} />
                  )}
                </Typography>
                {availableTopics.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                    {availableTopics.map((t) => (
                      <Chip
                        key={t.name}
                        label={t.name}
                        size="small"
                        onClick={() => handleTopicToggle(t.name)}
                        color={
                          selectedTopics.includes(t.name) ? "primary" : "default"
                        }
                        variant={
                          selectedTopics.includes(t.name) ? "filled" : "outlined"
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Type a topic and press Enter or Add..."
                    fullWidth
                    value={customTopicInput}
                    onChange={(e) => setCustomTopicInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCustomTopic();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                  <Tooltip title="Add topic">
                    <span>
                      <IconButton
                        onClick={handleAddCustomTopic}
                        disabled={!customTopicInput.trim()}
                        color="primary"
                        sx={{
                          border: "1px solid",
                          borderColor: "primary.main",
                          borderRadius: "8px",
                          "&.Mui-disabled": {
                            borderColor: (theme) => theme.palette.mode === "dark" ? "#444" : "#E0E0E0",
                          },
                        }}
                      >
                        <Add />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {selectedTopics.filter(
                  (t) => !availableTopics.find((a) => a.name === t)
                ).length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}
                  >
                    {selectedTopics
                      .filter((t) => !availableTopics.find((a) => a.name === t))
                      .map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          color="secondary"
                          onDelete={() =>
                            setSelectedTopics((prev) =>
                              prev.filter((x) => x !== t)
                            )
                          }
                        />
                      ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Instruction */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Instructions (optional)"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Any specific instructions for students..."
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            {/* Extra context — collapsible chat-style input */}
            <Accordion
              elevation={0}
              sx={{
                border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
                borderRadius: "10px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ChatBubbleOutline sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Add more context (optional)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Describe any special requirements, difficulty level, style of questions, or any other details..."
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
              </AccordionDetails>
            </Accordion>

            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <FilledButton
              fullWidth
              onClick={handleGenerate}
              disabled={isStreaming || !canSubmit}
              startIcon={
                isStreaming ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AutoAwesome />
                )
              }
              sx={{ height: 48, fontSize: "1rem", fontWeight: 600 }}
            >
              {isStreaming ? "Creating Quiz..." : "Create Quiz with AI"}
            </FilledButton>
          </Box>
        </Grid>

        {/* ── AI Progress & Result Panel ── */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
              borderRadius: "12px",
              border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
              p: 3,
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {!aiOutput && !createdQuiz && !executingTool && !isStreaming && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  color: "#95969D",
                  minHeight: 180,
                }}
              >
                <SmartToy sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography variant="body2" sx={{ textAlign: "center" }}>
                  Fill in the form and click &ldquo;Create Quiz with AI&rdquo;
                </Typography>
              </Box>
            )}

            {(isStreaming || executingTool) && !createdQuiz && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "primary.main" }}
                >
                  {executingTool
                    ? toolCallLabel(executingTool)
                    : "Generating quiz..."}
                </Typography>
              </Box>
            )}

            {aiOutput && (
              <Box
                sx={{
                  "& p": { m: 0, mb: 1, "&:last-child": { mb: 0 } },
                  "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
                  "& strong": { fontWeight: 600 },
                  "& h1, & h2, & h3, & h4": {
                    mt: 1.5,
                    mb: 0.5,
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  },
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                  color: "text.primary",
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {aiOutput}
                </ReactMarkdown>
              </Box>
            )}

            {aiOutput && !createdQuiz && !isStreaming && hasApprovedTopics && (isOffline || sessionToolsUsed.has("get_classroom_students")) && (
              <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConfirm}
                  sx={{ flex: 1, height: 44, fontWeight: 600, textTransform: "none", borderRadius: "10px" }}
                >
                  Confirm & Create Quiz
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => { setAiOutput(""); setPendingMessage(""); setHasApprovedTopics(false); setSessionToolsUsed(new Set()); }}
                  sx={{ height: 44, textTransform: "none", borderRadius: "10px" }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {createdQuiz && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a3d2a" : "#E8F5E9",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle sx={{ color: "#4CAF50" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Quiz created successfully!
                  </Typography>
                </Box>
                <FilledButton
                  size="small"
                  endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                  onClick={() =>
                    navigate(`/lms/quizzes/view/${createdQuiz.quiz_id}`)
                  }
                  sx={{ textTransform: "none", fontSize: "0.8rem", height: 36 }}
                >
                  View Quiz
                </FilledButton>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AIQuiz;
